<script setup>
import axios from "axios";
import { ref, reactive, watch, computed, onMounted } from "vue";
import VueCtkDateTimePicker from "vue-ctk-date-time-picker";
import "vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css";
import { useStore } from "@/store/authStore";
import { useCodeStore } from "@/store/codeStore";
import { useRouter } from "vue-router";
import { format } from "date-fns";
import imageFunction from "@/components/image.js";
import useRole from "@/components/role";
import { useRoleStore } from "@/store/roleStore";
import { useApiStore } from "@/store/apiStore";
const apiStore = useApiStore();
const roleStore = useRoleStore();
const { hasPermission } = useRole();

// ルーティングモジュールの使用

const router = useRouter();

// ストアの使用
const store = useStore();
const codeStore = useCodeStore();

const { getImageFromUrl } = imageFunction();

// トークンヘッダの取得
const tokenHeaders = store.getTokenHeaders;

/**
 * URL変更時、すぐに検索処理をしたくない場合のフラグ
 */
const isRoutePushed = ref(false);
const apiUrl = apiStore.getYoutubePath; // `http://boatrace-prod-public-alb-958219121.ap-northeast-1.elb.amazonaws.com/api/v1/youtube`

// 初期値の設定
const searchArticles = reactive({
  idTitle: null,
  kind: null,
  category: null,
  tag: null,
  pr: null,
  series: null,
  notNews: null,
  raceDate: null,
  boatracePlace: null,
  raceNo: null,
  toFro: null,
  commentKind: null,
  writer: null,
  status: null,
  fromDate: null,
  toDate: null,
  approval: null,
  newIcon: null,
  page: null,
  size: null,
});

/**
 * 日付文字列を 'yyyy/MM/dd HH:mm' のフォーマットに変換します。
 *
 * @param {string} dateStr - 変換する日付の文字列。例: "2023-08-07T02:21:10.086529"
 * @returns {string} 'yyyy/MM/dd HH:mm' のフォーマットに変換された日付文字列。入力が無効または空の場合は空文字を返します。
 */
const formattedDate = (dateStr) => {
  if (!dateStr) return "";
  return format(new Date(dateStr), "yyyy/MM/dd HH:mm");
};

// URLからクエリパラメータを取得
const props = defineProps({
  query: Object,
});
const query = reactive(props.query);

// ユーザーが検索を行ったかの判断
const hasSearched = ref(false);
/**
 * 検索中フラグ
 */
const searchNow = ref(false);
//検索結果
const results = ref(null);
//検索結果の記事の総数
const totalArticles = ref(0);
/**
 * 表示しているページ
 */
const currentPage = ref(1);
//1ページ当たりの記事数
const itemsPerPage = 10;

// 依存関係プロパティの計算
const allPages = computed(() => Math.ceil(totalArticles.value / itemsPerPage));

// チェックボックス関連のリアクティブ変数とウォッチャ
const checkboxPr = ref(false);
const checkboxNotNews = ref(false);
watch(checkboxPr, (newValue) => (searchArticles.pr = newValue ? 1 : 0));
watch(
  checkboxNotNews,
  (newValue) => (searchArticles.notNews = newValue ? 1 : 0)
);

// コードストアからのプルダウンデータの取得
const places = codeStore.places;
const commentKinds = codeStore.commentKind;
const toFros = codeStore.toFro;
const categories = codeStore.category.filter((item) => item.text === "動画");
const newIcons = codeStore.newIcon;
const status = [
  { value: 1, text: "公開" },
  { value: 2, text: "待機" },
  { value: 3, text: "終了" },
];

const imageSrcs = ref([]);
const raceNos = codeStore.raceNo;

/**
 * 指定された値に対応するテキストをリストから検索します。
 *
 * @function
 * @param {(string|number)} value - 検索するための値。
 * @param {Array<{value: string, text: string}>} list - 検索対象のリスト。valueとtextのペアを持つオブジェクトの配列として定義します。
 * @returns {string} - 対応するテキストが見つかった場合はそのテキストを、見つからなかった場合は元の値を返します。
 */
const getMatchedText = (value, list) => {
  if (!value) {
    return "";
  }
  if (value === null || value === undefined) {
    return ""; // あるいは、適切なデフォルト値をここで返す
  }
  const trimmedValue = value.toString().trim();
  const item = list.find((p) => p.value == trimmedValue);
  return item ? item.text : value;
};

const onChange = (event) => {
  const value = event.target.value;
  const currentDate = new Date();

  switch (value) {
    case "1": {
      searchArticles.fromDate = null;
      searchArticles.toDate = null;
      break;
    }
    case "2": {
      const oneYearAgo = new Date(
        currentDate.setFullYear(currentDate.getFullYear() - 1)
      );
      oneYearAgo.setHours(22, 0, 0, 0); // 22:00
      searchArticles.fromDate = oneYearAgo;
      break;
    }
    case "3": {
      const threeYearsAgo = new Date(
        currentDate.setFullYear(currentDate.getFullYear() - 3)
      );
      threeYearsAgo.setHours(22, 0, 0, 0); // 22:00
      searchArticles.fromDate = threeYearsAgo;
      break;
    }
  }
};

// ページネーション関連のメソッド

const visiblePages = computed(() => {
  let pages = [];

  if (allPages.value <= 7) {
    return Array.from({ length: allPages.value }, (_, i) => i + 1);
  }

  // 初めの5ページの場合
  if (currentPage.value <= 5) {
    for (let i = 1; i <= 7; i++) {
      pages.push(i);
    }
    if (allPages.value > 7) {
      pages.push("...");
      pages.push(allPages.value);
    }
  }
  // 最後の5ページの場合
  else if (currentPage.value >= allPages.value - 4) {
    pages.push(1);
    if (allPages.value > 7) {
      pages.push("...");
    }
    for (let i = allPages.value - 6; i <= allPages.value; i++) {
      pages.push(i);
    }
  }
  // 中間のページの場合
  else {
    pages.push(1);
    pages.push("...");
    for (let i = currentPage.value - 2; i <= currentPage.value + 2; i++) {
      pages.push(i);
    }
    if (currentPage.value + 2 < allPages.value - 2) {
      pages.push("...");
      pages.push(allPages.value);
    }
  }

  return pages;
});

const isLinkDisabled = (page) =>
  page === "..." || (currentPage.value === page && searchNow.value);

/**
 * 次のページへ遷移
 */
const nextPage = async () => {
  if (currentPage.value < allPages.value) {
    currentPage.value++;
    searchArticles.page = currentPage.value;
    await search(false);
    // 検索後にcurrentPageとallPagesをチェックして調整
    if (currentPage.value > allPages.value) {
      currentPage.value = allPages.value;
      searchArticles.page = allPages.value;
      await search(false);
    }
  }
};
/**
 * 前のページへ遷移
 */
const prevPage = async () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    searchArticles.page = currentPage.value;
    await search(false);
    // 検索後にcurrentPageとallPagesをチェックして調整
    if (currentPage.value > allPages.value) {
      currentPage.value = allPages.value;
      searchArticles.page = allPages.value;
      await search(false);
    }
  }
};
/**
 * 指定されたページにジャンプする関数
 * @param {number} pageNumber - 移動したいページ番号
 */
const jumpToPage = async (pageNumber) => {
  currentPage.value = pageNumber;
  searchArticles.page = currentPage.value;
  await search(false);
  // 検索後にcurrentPageとallPagesをチェックして調整
  if (currentPage.value > allPages.value) {
    currentPage.value = allPages.value;
    searchArticles.page = allPages.value;
    await search(false);
  }
};
/**
 * 検索ボタン押下時の処理
 */
const searched = () => {
  hasSearched.value = true;
  searchArticles.page = 1;
  currentPage.value = 1;
  search(true);
};

/**
 * クリアボタン押下時の処理
 */
const clear = () => {
  for (const key in searchArticles) {
    searchArticles[key] = null;
  }
  isRoutePushed.value = true;
  router.push({ name: "searchYoutube", query: filterArticles(searchArticles) });
};

/**
 * 空の項目を除去して検索条件をフィルタリング
 * @param {object} articles - 検索条件
 * @returns {object} - フィルタリングされた検索条件
 */
const filterArticles = (articles) => {
  return Object.fromEntries(
    Object.entries(articles).filter(
      // eslint-disable-next-line no-unused-vars
      ([key, value]) => value && value.toString().trim() !== ""
    )
  );
};

// axiosの設定オブジェクト
const config = reactive({
  params: searchArticles,
  headers: tokenHeaders.headers,
});

const editYoutube = (id) => {
  router.push({ name: "YoutubeInput", params: { id: id } });
};

const deleteYoutube = async (id) => {
  if (!window.confirm("削除しますか？")) {
    return;
  }
  try {
    const response = await axios.delete(apiUrl + `/${id}`, tokenHeaders);
    if (response.status == 200) {
      alert("削除しました。");
      await search(false);

      // 現在のページが新しい最後のページよりも大きい場合
      if (currentPage.value > allPages.value) {
        currentPage.value = allPages.value;
        searchArticles.page = allPages.value;
        // 新しいページの結果を取得
        await search(false);
      }
    }
  } catch (error) {
    alert("削除に失敗しました。", error);
  }
};

const getCategoryUrlPart = (value) => {
  switch (value) {
    case "01":
    case "02":
      return "news";
    case "03":
      return "column";
    case "04":
      return "movie";
    default:
      return ""; // あるいはエラーハンドリングを追加することができます。
  }
};

const previewYoutube = async (id, categoryValue) => {
  try {
    const apiUrlPre = apiStore.getIssuekeyPath;
    const response = await axios.get(apiUrlPre, tokenHeaders);
    if (response.status == 200) {
      const urlPart = getCategoryUrlPart(categoryValue);
      const previeUrl = `${process.env.VUE_APP_PREVIEW_SERVER}/article/${urlPart}/${id}?key=${response.data.key}`;
      window.open(previeUrl, "_blank");
    }
  } catch (error) {
    alert("キー取得に失敗しました。", error);
  }
};

/**
 * 記事の検索処理を実行
 * 1. 現在の検索条件をフィルタリングしてURLにクエリパラメータを追加
 * 2. axiosを使用してAPIから記事を取得。
 * 3. 正常なレスポンスが得られた場合、記事のデータを保存
 * 4. エラーが発生した場合は、エラー内容をログに出力し、エラーハンドリング
 *
 * @async
 *  * @param {boolean} isFromSearchButton - 検索ボタンからの呼び出し時にtrue
 */
const search = async (isFromSearchButton = false) => {
  // imageSrcsの初期化
  imageSrcs.value = [];

  searchNow.value = true;
  isRoutePushed.value = true;

  if (isFromSearchButton) {
    const filteredArticles = filterArticles(searchArticles);
    // 検索ボタン押下時だけ、クエリパラメータを更新
    config.params = filteredArticles;
  } else {
    // それ以外の場合は、pageのみを更新
    config.params.page = searchArticles.page;
  }

  //検索処理
  try {
    const response = await axios.get(apiUrl, config);

    if (!response) {
      return;
    }

    if (response.status == 200) {
      totalArticles.value = response.data.totalVideos;
      results.value = response.data.youtube;
      results.value.forEach(async (result, index) => {
        if (result.thumbnail) {
          const responseImage = await getImageFromUrl(result.thumbnail);
          imageSrcs.value[index] = responseImage;
        }
      });
    }
  } catch (error) {
    console.error(error);
    alert(error.message);
    if (!error.response) {
      alert("原因不明のエラー");

      return;
    }
    // エラーオブジェクトからステータスコードを取得
    const statusCode = error.response && error.response.status;
    // ステータスコードに応じたエラーハンドリング
    if (statusCode) {
      switch (statusCode) {
        case 400:
          alert("Bad Request - リクエストが不正です");
          break;
        case 401:
          alert("Unauthorized - 認証エラーです");
          break;
        case 403:
          alert("Forbidden - アクセス権限がありません");
          break;
        case 404:
          alert("Not Found - リソースが見つかりません");
          break;
        case 500:
          alert("Internal Server Error - サーバーエラーです");
          break;
        default:
          console.error(`Unknown error - ステータスコード: ${statusCode}`);
      }
    } else {
      alert("エラーが発生しましたが、ステータスコードを取得できませんでした");
    }
  } finally {
    searchNow.value = false;
  }
};

/**
 * nullの項目を除去してクエリパラメータをフィルタリング
 * @param {object} queryParams - クエリパラメータ
 * @returns {object} - フィルタリングされたクエリパラメータ
 */
const filterQueryParams = (queryParams) => {
  return Object.fromEntries(
    // eslint-disable-next-line no-unused-vars
    Object.entries(queryParams).filter(([key, value]) => value !== null)
  );
};

// searchQueryが変更された時にURLのクエリを更新する
watch(config.params, (newVal) => {
  router.replace({
    name: "searchYoutube",
    query: filterQueryParams(newVal),
  });
});

onMounted(async () => {
  hasSearched.value = true;

  // フラグが設定されている場合は特定の処理をスキップ
  if (isRoutePushed.value) {
    isRoutePushed.value = false; // フラグをリセット
    return;
  }
  config.params = query;

  for (const key in query) {
    // searchArticlesが該当のキーを持っている場合のみ値をコピー
    if (key in searchArticles) {
      searchArticles[key] = query[key];
    }
    // pageキーがqueryに存在する場合
    if (key === "page") {
      currentPage.value = parseInt(query[key], 10); // 文字列を整数に変換して代入
    }
  }
  try {
    await search(true);
  } catch (error) {
    console.error(error);
  }
});
</script>

<template>
  <div class="content_parent registration d-flex flex-column">
    <div class="content_line">
      <div class="item_parent item_parent_long">
        <div class="content_title">
          <div class="texttitle">ID・タイトル</div>
        </div>
        <div class="content_item">
          <input
            class="form-control"
            v-model="searchArticles.idTitle"
            maxlength="100"
          />
        </div>
      </div>
    </div>
    <div class="content_line">
      <div class="item_parent item_parent_long">
        <div class="content_title">
          <div class="texttitle">シリーズ</div>
        </div>
        <div class="content_item">
          <input
            class="form-control"
            v-model="searchArticles.series"
            name="series"
            maxlength="40"
          />
        </div>
      </div>
    </div>
    <div class="content_line">
      <div class="item_parent item_parent_long">
        <div class="content_title">
          <div class="texttitle">著者</div>
        </div>
        <div class="content_item">
          <input
            class="form-control"
            v-model="searchArticles.writer"
            name="writer"
            maxlength="20"
          />
        </div>
      </div>
    </div>
    <div class="content_line">
      <div class="item_parent item_parent_long">
        <div class="content_title">
          <div class="texttitle">タグ</div>
        </div>
        <div class="content_item">
          <input
            class="form-control"
            v-model="searchArticles.tag"
            name="tag"
            maxlength="50"
          />
        </div>
      </div>
    </div>

    <div v-if="false" class="content_line d-flex flex-row">
      <div class="content_title">
        <div class="texttitle_wrap">種別</div>
      </div>
      <div class="d-flex flex-row flex-wrap">
        <div v-if="false" class="item_parent">
          <div class="row_texttitle">種別</div>
          <div class="content_item">
            <select class="form-select" v-model="searchArticles.kind" disabled>
              <option value="01" selected>フリー</option>
              <option value=""></option>
            </select>
          </div>
        </div>
        <div v-if="false" class="item_parent">
          <div class="row_texttitle">カテゴリ</div>
          <div class="content_item">
            <select class="form-select" v-model="searchArticles.category">
              <option value=""></option>
              <option
                v-for="category in categories"
                :value="category.value"
                :key="category.text"
              >
                {{ category.text }}
              </option>
            </select>
          </div>
        </div>
        <div class="item_parent">
          <div class="row_texttitle">コメント種別</div>
          <div class="content_item">
            <select
              v-model="searchArticles.commentKind"
              class="form-select"
              name="comment_kind"
            >
              <option value=""></option>
              <option
                v-for="commentKind in commentKinds"
                :value="commentKind.value"
                :key="commentKind.text"
              >
                {{ commentKind.text }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>
    <div class="content_line d-flex flex-row">
      <div class="content_title">
        <div class="texttitle_wrap">公開日時</div>
      </div>
      <div class="d-flex flex-row flex-wrap">
        <div class="item_parent me-3">
          <div class="content_item">
            <div class="d-flex flex-row mt-1">
              <VueCtkDateTimePicker
                button-now-translation="現在時刻"
                v-model="searchArticles.fromDate"
                :button-clear="false"
                format="YYYY-MM-DD"
                formatted="YYYY年MM月DD日"
                label="日付を選択"
                :no-header="true"
                id="fromDate"
                only-date
                no-label
                auto-close
              />

              <span class="span-nami">～</span>
              <VueCtkDateTimePicker
                button-now-translation="現在時刻"
                v-model="searchArticles.toDate"
                label="日付を選択"
                :button-clear="false"
                format="YYYY-MM-DD"
                formatted="YYYY年MM月DD日"
                :no-header="true"
                id="toDate"
                only-date
                no-label
                auto-close
              />
            </div>
          </div>
        </div>
        <div class="item_parent d-flex flex-row align-items-center m-3">
          <div class="form-check">
            <input
              class="form-check-input"
              id="scope1"
              type="radio"
              name="scope"
              value="1"
              @change="onChange"
              selected
            />
            <label class="form-check-label" for="scope1">すべて</label>
          </div>
          <div class="form-check">
            <input
              class="form-check-input"
              id="scope2"
              type="radio"
              name="scope"
              value="2"
              @change="onChange"
            />
            <label class="form-check-label" for="scope2">1年以内</label>
          </div>
          <div class="form-check">
            <input
              class="form-check-input"
              id="scope3"
              type="radio"
              name="scope"
              value="3"
              @change="onChange"
            />
            <label class="form-check-label" for="scope3">3年以内</label>
          </div>
        </div>
      </div>
    </div>
    <div class="content_line d-flex flex-row">
      <div class="content_title">
        <div class="texttitle_wrap">レース</div>
      </div>
      <div class="d-flex flex-row flex-wrap align-items-center">
        <div class="item_parent">
          <div class="row_texttitle">レース日</div>
          <div class="content_item">
            <VueCtkDateTimePicker
              button-now-translation="現在時刻"
              v-model="searchArticles.raceDate"
              label="日付を選択"
              :button-clear="false"
              format="YYYY-MM-DD"
              formatted="YYYY年MM月DD日"
              :no-header="true"
              :no-button-now="true"
              id="raceDate"
              only-date
              no-label
              auto-close
            />
          </div>
        </div>
        <div class="item_parent">
          <div class="row_texttitle">レース場</div>
          <div class="content_item">
            <select
              v-model="searchArticles.boatracePlace"
              class="form-select"
              name="boatrace_course"
            >
              <option value=""></option>
              <option
                v-for="place in places"
                :value="place.value"
                :key="place.text"
              >
                {{ place.text }}
              </option>
            </select>
          </div>
        </div>
        <div class="item_parent">
          <div class="row_texttitle">レース番号</div>

          <div class="content_item">
            <select
              v-model="searchArticles.raceNo"
              class="form-select"
              name="race_no"
            >
              <option value="0"></option>
              <option
                v-for="raceNo in raceNos"
                :value="raceNo.value"
                :key="raceNo.text"
              >
                {{ raceNo.text }}
              </option>
            </select>
          </div>
        </div>
        <div v-if="false" class="item_parent">
          <div class="row_texttitle">レース前/後</div>
          <div class="content_item">
            <select
              v-model="searchArticles.toFro"
              class="form-select"
              name="to_fro"
            >
              <option value=""></option>
              <option
                v-for="toFro in toFros"
                :value="toFro.value"
                :key="toFro.text"
              >
                {{ toFro.text }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>
    <div class="content_line d-flex flex-row">
      <div class="content_title">
        <div class="texttitle_wrap">状態</div>
      </div>
      <div class="d-flex flex-row flex-wrap">
        <div class="item_parent">
          <div class="row_texttitle">状態</div>
          <div class="content_item">
            <select v-model="searchArticles.status" class="form-select">
              <option value=""></option>
              <option value="1">公開</option>
              <option value="2">待機</option>
              <option value="3">終了</option>
            </select>
          </div>
        </div>
        <div class="item_parent">
          <div class="row_texttitle">承認</div>
          <div class="content_item">
            <select
              :disabled="!hasPermission(roleStore.role, 'PUBLISH_INSTANTLY')"
              v-model="searchArticles.approval"
              class="form-select"
              name="approval"
            >
              <option value=""></option>
              <option value="1">承認</option>
              <option value="2">未承認</option>
              <option value="3">却下</option>
            </select>
          </div>
        </div>
        <div class="item_parent">
          <div class="row_texttitle">NEWアイコン表示</div>
          <div class="content_item">
            <select
              v-model="searchArticles.newIcon"
              class="form-select"
              name="new"
            >
              <option value=""></option>
              <option
                v-for="newIcon in newIcons"
                :value="newIcon.value"
                :key="newIcon.text"
              >
                {{ newIcon.text }}
              </option>
            </select>
          </div>
        </div>

        <div v-if="false" class="item_parent d-flex flex-column">
          <div class="content_item">
            <label for="notNews" class="row_texttitle m-0"
              >ニュースに掲載しない</label
            >
            <input
              class="form-control"
              v-model="checkboxNotNews"
              type="checkbox"
              id="notNews"
            />
          </div>
          <div class="message_sub">(公式、ゲンダイニュース対象)</div>
        </div>
        <div class="content_item">
          <div class="form-check">
            <label for="pr" class="ms-2 font-bold form-check-label">PR</label>
            <input
              class="ms-1 form-check-input"
              v-model="checkboxPr"
              type="checkbox"
              id="pr"
            />
          </div>
          <div class="message_sub"></div>
        </div>
      </div>
    </div>
  </div>
  <div class="button_box">
    <div class="d-flex flex-row justify-content-center">
      <button class="submit_button" @click="searched" :disabled="searchNow">
        検索
      </button>
      <button class="submit_button" @click="clear">クリア</button>
    </div>
  </div>

  <div class="content_parent">
    <p :style="searchNow ? {} : { opacity: 0 }">検索中...</p>
    <p class="search_result">検索結果:{{ totalArticles }}件</p>
    <div class="results_box" v-if="allPages && hasSearched">
      <!--  <nav aria-label="Page navigationd-flex">
        <ul class="pagination justify-content-center">
          <li class="page-item">
            <a class="page-link" @click="prevPage" :disabled="currentPage == 1 && searchNow" aria-label="Previous">
              <span aria-hidden="true">&laquo;</span>
            </a>
          </li>
          <li v-if="allPages" v-for="page in allPages" :key="page" class="page-item">
            <a class="page-link" :class="{ active: currentPage == page }" @click="jumpToPage(page)"
              :disabled="currentPage == page && searchNow">
              {{ page }}
            </a>
          </li>
          <li class="page-item">
            <a class="page-link" @click="nextPage" :disabled="currentPage == allPages && searchNow" aria-label="Next">
              <span aria-hidden="true">&raquo;</span>
            </a>
          </li>
        </ul>
      </nav>-->

      <nav aria-label="Page navigationd-flex">
        <ul class="pagination justify-content-center">
          <li class="page-item">
            <a
              class="page-link"
              @click="prevPage"
              :disabled="currentPage == 1 && searchNow"
              aria-label="Previous"
            >
              <span aria-hidden="true">&laquo;</span>
            </a>
          </li>
          <li
            v-for="(page, index) in visiblePages"
            :key="index"
            class="page-item"
          >
            <a
              v-if="page"
              class="page-link"
              :class="{
                active: currentPage == page,
                'disabled-link': isLinkDisabled(page),
              }"
              @click="jumpToPage(page)"
            >
              {{ page }}
            </a>
          </li>
          <li class="page-item">
            <a
              class="page-link"
              @click="nextPage"
              :disabled="currentPage == allPages && searchNow"
              aria-label="Next"
            >
              <span aria-hidden="true">&raquo;</span>
            </a>
          </li>
        </ul>
      </nav>

      <div class="fixed-table table-responsive">
        <table
          class="result_table table-striped table-bordered"
          aria-colspan="8"
          style="border: none"
        >
          <thead>
            <tr class="result_table_th">
              <th scope="col" class="fixed-left fixed-left-title">ID</th>
              <th scope="col" class="long_th">タイトル</th>
              <th scope="col image-th">画像</th>
              <th scope="col">カテゴリ</th>
              <th scope="col" class="long_th">シリーズ</th>
              <th scope="col">公開日時</th>
              <th scope="col">状態</th>
              <th scope="col" class="long_th">タグ</th>
              <th scope="col">レース場</th>
              <th scope="col" class="writer-th">著者</th>
              <th scope="col">更新日時</th>
              <th scope="col" class="fixed-right fixed-right-title">Actions</th>
            </tr>
          </thead>
          <tbody class="result_tbody_bgcolor">
            <tr v-for="(result, index) in results" :key="index" :index="index">
              <td
                scope="row"
                class="text_center fixed-left"
                v-text="result.id"
              ></td>
              <td class="long_th" v-text="result.title"></td>
              <td><img :src="imageSrcs[index]" width="80" /></td>
              <td
                class="text_center"
                v-text="getMatchedText(result.category, categories)"
              ></td>
              <td class="long_th" v-text="result.series"></td>
              <td class="text_center">
                {{ formattedDate(result.releaseDate) }}
              </td>
              <td
                class="text_center"
                v-text="getMatchedText(result.status, status)"
              ></td>
              <td class="long_th" v-text="result.tag"></td>
              <td
                class="text_center"
                v-text="getMatchedText(result.boatracePlace, places)"
              ></td>
              <td v-text="result.writer"></td>
              <td class="text_center">
                {{ formattedDate(result.updateDate) }}
              </td>
              <td class="button_position fixed-right d-flex flex-column">
                <button
                  type="button"
                  class="submit_button submit_button_sub"
                  @click="editYoutube(result.id)"
                >
                  編集
                </button>
                <button
                  @click="previewYoutube(result.id, result.category)"
                  class="submit_button submit_button_sub"
                >
                  プレビュー
                </button>
                <button
                  :disabled="!hasPermission(roleStore.role, 'ARTICLE_DELETE')"
                  @click="deleteYoutube(result.id)"
                  class="submit_button submit_button_sub"
                >
                  削除
                </button>
                <!--:disabled="!hasPermission(roleStore.role, 'ARTICLE_DELETE')"-->
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
